// Full-page gate shown in place of the app shell when the signed-in user's
// OWN account (userAccount/{uid}.status, via ensureUserAccount -- Task 6/7)
// is 'deleted'. Mounted in App.js BEFORE the workspace switcher renders: a
// deleted account's workspaces are unreadable under the rules layer, so
// without this gate the user would land in empty-state onboarding instead of
// being told their account is deleted and when it purges.

import React from 'react';
import { SUPPORT_EMAIL } from '../../../config/support';

export function formatPurgeDate(purgeAfter) {
    if (!purgeAfter) return '';
    const date = new Date(purgeAfter);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// The two variants of this screen, mirroring how AccountStatusBanner already
// distinguishes a cascaded suspension from a direct one.
//
// isCascaded is the team-agent case: their OWN userAccount is `active`, so
// this screen never gated for them, while every workspace they own was
// cascade-deleted with their payer and is excluded from `list` by the rules.
// They used to land on "Create your first team" with no explanation at all.
// The copy must not blame them -- nothing about their own account is wrong --
// and it must not quote a purge date, because the retention clock belongs to
// the payer's account, which this user cannot read.
export function deletedCopyFor({ purgeAfter, statusReason, isCascaded } = {}) {
    const purgeDate = formatPurgeDate(purgeAfter);
    const reason = typeof statusReason === 'string' ? statusReason.trim() : '';

    if (isCascaded) {
        return {
            heading: "Your team's account has been deleted",
            body: "Your workspace belongs to a team whose KeyFlow account has been deleted, so your transactions, contacts, and reports are no longer available here. Contact your team's owner, or KeyFlow support, if you believe this is a mistake.",
            reason,
        };
    }

    return {
        heading: 'This account has been deleted',
        body: purgeDate
            ? `Your transactions, contacts, and reports will be permanently removed on ${purgeDate}. Contact support before then if you believe this is a mistake.`
            : 'Your transactions, contacts, and reports are scheduled for permanent removal. Contact support if you believe this is a mistake.',
        reason,
    };
}

export default function AccountDeletedScreen({ purgeAfter, statusReason, isCascaded }) {
    const { heading, body, reason } = deletedCopyFor({ purgeAfter, statusReason, isCascaded });
    return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--color-light)] px-4">
            <div className="max-w-md rounded-lg bg-[color:var(--surface-primary)] p-10 text-center shadow-xl">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--surface-danger)]">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        focusable="false"
                        className="text-[color:var(--color-danger)]"
                    >
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    </svg>
                </div>
                <h1 className="mt-4 text-xl font-semibold text-[color:var(--text-primary)]">
                    {heading}
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--text-muted)]">
                    {body}
                </p>
                {reason && (
                    <p className="mt-3 text-sm leading-relaxed text-[color:var(--text-muted)]">
                        Reason: {reason}
                    </p>
                )}
                {/* Both copy variants tell the user to contact support, so the
                    address has to actually appear — and this screen is the one
                    with a deadline attached, where reaching a human matters
                    most. */}
                <p className="mt-4 text-sm text-[color:var(--text-muted)]">
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                        {SUPPORT_EMAIL}
                    </a>
                </p>
            </div>
        </div>
    );
}
